import React from 'react';
import { Link } from 'react-router-dom';
import './Products.css';

// ── Product categories ────────────────────────────────────────────────────────

interface ProductCategory {
  id: string;
  name: string;
  description: string;
  image: string;
  link?: string;
}

const productCategories: ProductCategory[] = [
  {
    id: 'truck-scales',
    name: 'Básculas Camioneras',
    description: 'Sistemas de pesaje para vehículos pesados y aplicaciones industriales de gran capacidad',
    image: '/images/truck-scales-small.jpg',
    link: '/productos/basculas-camioneras',
  },
  {
    id: 'counting-scales',
    name: 'Básculas Contadoras',
    description: 'Equipos especializados para conteo preciso de piezas y componentes',
    image: '/images/counting-scales-small.jpg',
    link: '/productos/basculas-contadoras',
  },
  {
    id: 'belt-scales',
    name: 'Básculas de Banda',
    description: 'Sistemas de pesaje dinámico para transportadores y líneas de producción',
    image: '/images/conveyor-scales-small.jpg',
    link: '/productos/basculas-de-banda',
  },
  {
    id: 'bench-scales',
    name: 'Básculas de Banco',
    description: 'Equipos de mesa para aplicaciones de laboratorio y control de calidad',
    image: '/images/bench-scales-small.jpg',
    link: '/productos/basculas-de-banco',
  },
  {
    id: 'floor-scales',
    name: 'Básculas de Piso',
    description: 'Plataformas de pesaje para aplicaciones industriales y comerciales',
    image: '/images/platform-scales-small.jpg',
    link: '/productos/basculas-de-piso',
  },
  {
    id: 'monorail-scales',
    name: 'Básculas de Monorriel',
    description: 'Sistemas de pesaje suspendido para industrias cárnicas y alimentarias',
    image: '/images/monorail-scales.jpg',
    link: '/productos/basculas-de-monorriel',
  },
  {
    id: 'forklift-scales',
    name: 'Básculas de Montacargas',
    description: 'Equipos integrados para pesaje durante el transporte con montacargas',
    image: '/images/forklift-scales-small.jpg',
    link: '/productos/basculas-de-montacargas',
  },
  {
    id: 'railway-scales',
    name: 'Básculas para Ferrocarril',
    description: 'Sistemas especializados para pesaje de vagones y carros ferroviarios',
    image: '/images/railway-scales-small.jpg',
    link: '/productos/basculas-para-ferrocarril',
  },
  {
    id: 'checkweighing-scales',
    name: 'Básculas Verificadoras',
    description: 'Equipos de verificación automática de peso en líneas de producción',
    image: '/images/checkweigher-scales.jpg',
    link: '/productos/basculas-verificadoras',
  },
  {
    id: 'load-cells',
    name: 'Celdas de Carga',
    description: 'Sensores de peso de alta precisión para aplicaciones industriales',
    image: '/images/weigh-bars.jpg',
    link: '/productos/celdas-de-carga',
  },
  {
    id: 'dynamometers',
    name: 'Dinamómetros',
    description: 'Instrumentos para medición de fuerza y tensión en aplicaciones industriales',
    image: '/images/dynamometers.jpg',
    link: '/productos/dinamometros',
  },
  {
    id: 'agricultural-equipment',
    name: 'Equipos Agropecuarios',
    description: 'Soluciones de pesaje especializadas para el sector agrícola y ganadero',
    image: '/images/agricultural-equipment.jpg',
    link: '/productos/equipos-agropecuarios',
  },
  {
    id: 'force-equipment',
    name: 'Equipo de Fuerza',
    description: 'Instrumentos para medición y control de fuerza en procesos industriales',
    image: '/images/force-equipment.jpg',
    link: '/productos/equipo-de-fuerza',
  },
  {
    id: 'printers',
    name: 'Impresoras',
    description: 'Equipos de impresión para etiquetas y comprobantes de pesaje',
    image: '/images/printers.jpg',
    link: '/productos/impresoras',
  },
  {
    id: 'weight-indicators',
    name: 'Indicadores de Peso',
    description: 'Displays y controladores electrónicos para sistemas de pesaje',
    image: '/images/indicators-small.jpg',
    link: '/productos/indicadores-de-peso',
  },
];

// ── Page ──────────────────────────────────────────────────────────────────────

const Products: React.FC = () => {
  return (
    <div className="products">

      {/* Hero */}
      <section className="products-hero">
        <div className="container">
          <div className="products-hero-eyebrow">Catálogo Completo</div>
          <h1>Equipo de Pesaje<br />Industrial</h1>
          <p>
            15 categorías de productos de marcas líderes mundiales para todas
            las aplicaciones industriales.
          </p>
        </div>
      </section>

      {/* Product grid */}
      <section className="products-content">
        <div className="container">
          <div className="products-content-header">
            <h2>Nuestro Catálogo</h2>
            <p>
              Desde básculas básicas hasta sistemas automatizados complejos,
              respaldados por soporte técnico certificado.
            </p>
          </div>
          <div className="products-grid">
            {productCategories.map((category) => (
              <div key={category.id} className="product-category">
                <div className="product-image">
                  <img src={category.image} alt={category.name} />
                </div>
                <div className="product-info">
                  <h3>{category.name}</h3>
                  <p>{category.description}</p>
                  {category.link ? (
                    <Link to={category.link} className="product-btn">Ver Detalles</Link>
                  ) : (
                    <button className="product-btn">Ver Detalles</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="products-stats">
        <div className="container">
          <div className="products-stats-grid">
            <div className="products-stat">
              <span className="products-stat-number">15</span>
              <span className="products-stat-label">Categorías de producto</span>
            </div>
            <div className="products-stat">
              <span className="products-stat-number">3</span>
              <span className="products-stat-label">Marcas líderes mundiales</span>
            </div>
            <div className="products-stat">
              <span className="products-stat-number">+30</span>
              <span className="products-stat-label">Años de experiencia</span>
            </div>
            <div className="products-stat">
              <span className="products-stat-number">Nacional</span>
              <span className="products-stat-label">Cobertura de servicio</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="products-why">
        <div className="container">
          <div className="products-why-header">
            <h2>Características de nuestros productos</h2>
            <p>
              Cada equipo es seleccionado y respaldado por nuestro equipo técnico
              para garantizar su rendimiento en el proceso industrial.
            </p>
          </div>
          <div className="products-why-grid">
            <div className="products-why-card">
              <div className="products-why-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <polyline points="9 12 11 14 15 10"/>
                </svg>
              </div>
              <h3>Calidad certificada</h3>
              <p>Productos de Avery Weigh-Tronix, Rice Lake y Dillon, fabricantes con certificaciones internacionales de calidad.</p>
            </div>
            <div className="products-why-card">
              <div className="products-why-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <h3>Precisión garantizada</h3>
              <p>Alta exactitud en todas las mediciones, respaldada por calibración certificada ante la EMA (M70).</p>
            </div>
            <div className="products-why-card">
              <div className="products-why-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
              </div>
              <h3>Soporte técnico directo</h3>
              <p>Asesoría experta y servicio profesional directo de fábrica. Contamos con un equipo de técnicos propios, certificados oficialmente por los fabricantes, para garantizar la máxima calidad y respaldo en cada atención..</p>
            </div>
            <div className="products-why-card">
              <div className="products-why-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </div>
              <h3>Instalación y puesta en marcha</h3>
              <p>Servicio integral desde la instalación hasta el mantenimiento preventivo y correctivo en sitio.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="products-cta">
        <div className="container">
          <h2>¿No encuentra lo que busca?</h2>
          <p>
            Nuestros especialistas le ayudarán a encontrar el equipo adecuado
            para su aplicación específica.
          </p>
          <div className="products-cta-actions">
            <Link to="/contacto" className="btn-primary">Solicitar Asesoría</Link>
            <a href="tel:+523336349050" className="btn-outline">Llamar: (33) 3634-9050</a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Products;
